import React from 'react';

import {
  Calendar,
  CaseSensitive,
  CheckSquare,
  ChevronDownSquare,
  CircleArrowUp,
  DollarSign,
  DraftingCompass,
  Globe,
  Hash,
  type LucideIcon,
  Percent,
  RulerDimensionLine,
  Sigma,
  SquareFunction,
  Star,
  Type,
  User,
} from 'lucide-react';

import { CalculationInsightCountFactors } from 'common/constants/ideaFormulaFields';
import { FieldDisplayMode, IdeaFieldTypes } from 'common/constants/ideas';
import { CalculationFunctions } from 'common/prioritization/CalculationFactorTypes';
import { Select, type SelectBaseOption } from 'common/uiV3/Select';
import capitalizeFirstLetter from 'common/util/capitalizeFirstLetter';

// Icon mapping between field type and Lucide icon
export const FieldTypeDataMap: Record<IdeaFieldTypes, { icon: LucideIcon; label: string }> = {
  [IdeaFieldTypes.boolean]: { icon: CheckSquare, label: 'Checkbox' },
  [IdeaFieldTypes.date]: { icon: Calendar, label: 'Date' },
  [IdeaFieldTypes.dropdown]: { icon: ChevronDownSquare, label: 'Dropdown' },
  [IdeaFieldTypes.multiselect]: { icon: ChevronDownSquare, label: 'Multiselect' },
  [IdeaFieldTypes.formula]: { icon: Sigma, label: 'Formula' },
  [IdeaFieldTypes.number]: { icon: Hash, label: 'Number' },
  [IdeaFieldTypes.score]: { icon: SquareFunction, label: 'Score' },
  [IdeaFieldTypes.fibonacci]: { icon: DraftingCompass, label: 'Fibonacci' },
  [IdeaFieldTypes.rating]: { icon: Star, label: 'Rating' },
  [IdeaFieldTypes.multilineText]: { icon: CaseSensitive, label: 'Multiline Text' },
  [IdeaFieldTypes.text]: { icon: Type, label: 'Text' },
  [IdeaFieldTypes.url]: { icon: Globe, label: 'URL' },
  [IdeaFieldTypes.user]: { icon: User, label: 'User' },
  [IdeaFieldTypes.range]: { icon: RulerDimensionLine, label: 'Range' },
} as const;

// Icon mapping between field modes and Lucide icon
export const FieldModeDataMap: Record<FieldDisplayMode, { icon: LucideIcon; label: string }> = {
  [FieldDisplayMode.standard]: { icon: Hash, label: 'Standard' },
  [FieldDisplayMode.percentage]: { icon: Percent, label: 'Percentage' },
  [FieldDisplayMode.currency]: { icon: DollarSign, label: 'Currency' },
};

// These are the factors that are common to all companies and are always available
export const CommonFactorOptions: SelectBaseOption[] = [
  {
    label: (
      <Select.OptionLabel>
        <CircleArrowUp />
        Company Insight Count
      </Select.OptionLabel>
    ),
    value: CalculationInsightCountFactors.companyInsightCount,
  },
  {
    label: (
      <Select.OptionLabel>
        <CircleArrowUp />
        Insight Count - All
      </Select.OptionLabel>
    ),
    value: CalculationInsightCountFactors.insightCount,
  },
  {
    label: (
      <Select.OptionLabel>
        <CircleArrowUp />
        Insight Count - Nice to have
      </Select.OptionLabel>
    ),
    value: CalculationInsightCountFactors.niceToHaveInsightCount,
  },
  {
    label: (
      <Select.OptionLabel>
        <CircleArrowUp />
        Insight Count - Important
      </Select.OptionLabel>
    ),
    value: CalculationInsightCountFactors.importantInsightCount,
  },
  {
    label: (
      <Select.OptionLabel>
        <CircleArrowUp />
        Insight Count - Must have
      </Select.OptionLabel>
    ),
    value: CalculationInsightCountFactors.mustHaveInsightCount,
  },
];

const CalculationFunctionOptions: SelectBaseOption[] = Object.values(CalculationFunctions).map(
  (functionName) => ({
    label: (
      <Select.OptionLabel>
        <Sigma />
        {capitalizeFirstLetter(functionName)}
      </Select.OptionLabel>
    ),
    value: functionName,
  }),
);

export const getCalculationFunctionOptions = (factor: string | null) => {
  const CountOption: SelectBaseOption = {
    label: (
      <Select.OptionLabel>
        <Sigma />
        Count
      </Select.OptionLabel>
    ),
    value: CalculationFunctions.count,
  };

  if (
    factor &&
    Object.values(CalculationInsightCountFactors).includes(factor as CalculationInsightCountFactors)
  ) {
    return [CountOption];
  }

  return CalculationFunctionOptions;
};
